import type { ReactNode } from "react";

/**
 * O molde das páginas de disciplina.
 *
 * Elas eram sete variações da mesma coisa, cada uma com listas de "o que
 * construímos" e pastilhas de tecnologia. Isso é enchimento: ninguém
 * contrata por causa de uma pastilha escrita "Hilt". Ficou o que importa —
 * uma promessa, o que eu entrego em três linhas, e o que já foi publicado,
 * com link para conferir.
 */
export function PaginaDisciplina({
  rotulo,
  titulo,
  intro,
  entrego,
  publicado,
  nota,
}: {
  rotulo: string;
  titulo: ReactNode;
  intro: ReactNode;
  entrego: readonly { titulo: string; corpo: string }[];
  publicado?: readonly {
    titulo: string;
    corpo: string;
    link?: string;
    marca?: string;
  }[];
  nota?: ReactNode;
}) {
  return (
    <section className="jg-shell pb-24 pt-12 sm:pt-16">
      <p className="jg-eyebrow">{rotulo}</p>

      <h1 className="mt-5 max-w-[17ch] font-display text-[clamp(2rem,5.5vw,3.2rem)] font-medium leading-[1.08] tracking-[-0.02em]">
        {titulo}
      </h1>

      <p className="mt-6 max-w-[58ch] text-[length:var(--text-step-0)] leading-[1.55] text-jg-fg-muted">
        {intro}
      </p>

      <div className="jg-grid mt-16 border-t border-jg-rule pt-8">
        <div className="col-span-full lg:col-span-5">
          <h2 className="jg-eyebrow">O que eu entrego</h2>
          <ul className="mt-5">
            {entrego.map((e) => (
              <li
                key={e.titulo}
                className="border-t border-jg-border py-4 first:border-t-0 first:pt-0"
              >
                <span className="font-medium">{e.titulo}</span>
                <span className="mt-1 block max-w-[46ch] text-sm leading-relaxed text-jg-fg-muted">
                  {e.corpo}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {publicado && publicado.length > 0 && (
          <div className="col-span-full mt-14 lg:col-span-6 lg:col-start-7 lg:mt-0">
            <h2 className="jg-eyebrow">Publicado</h2>
            <ul className="mt-5">
              {publicado.map((p) => (
                <li
                  key={p.titulo}
                  className="border-t border-jg-border py-5 first:border-t-0 first:pt-0"
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-display text-[length:var(--text-step-1)] font-medium leading-snug">
                      {p.link ? (
                        <a
                          className="underline decoration-jg-border decoration-1 underline-offset-[6px] transition-[text-decoration-color] duration-150 hover:decoration-jg-accent"
                          href={p.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {p.titulo} <span aria-hidden="true">↗</span>
                        </a>
                      ) : (
                        p.titulo
                      )}
                    </h3>
                    {p.marca && (
                      <span className="jg-eyebrow shrink-0">{p.marca}</span>
                    )}
                  </div>
                  <p className="mt-2 max-w-[52ch] text-sm leading-relaxed text-jg-fg-muted">
                    {p.corpo}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {nota && (
        <p className="mt-14 max-w-[58ch] border-t border-jg-border pt-7 text-sm leading-relaxed text-jg-fg-muted">
          {nota}
        </p>
      )}

      <p className="mt-16 border-t border-jg-rule pt-8 text-sm text-jg-fg-muted">
        Precisa construir algo assim?{" "}
        <a className="jg-link" href="/hire">
          Me conte o problema
        </a>
        .
      </p>
    </section>
  );
}
